"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { Media } from "@/lib/dto";
import { resolveMediaUrl } from "@/lib/media";
import { cn } from "@/lib/format";

type GalleryImage = {
  src: string;
  alt: string;
  caption: string | null;
};

export function ArticleMediaGallery({
  items,
  title = "Media gallery",
  className,
}: {
  items: Media[];
  title?: string;
  className?: string;
}) {
  const images = (items ?? [])
    .map((item) => {
      const src = item?.url ? resolveMediaUrl(item.url) : null;
      return src
        ? { src, alt: item?.alt || "", caption: item?.caption ?? null }
        : null;
    })
    .filter((item): item is GalleryImage => Boolean(item));
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const previous = useCallback(
    () =>
      setActiveIndex((index) =>
        index == null ? null : (index - 1 + images.length) % images.length,
      ),
    [images.length],
  );
  const next = useCallback(
    () =>
      setActiveIndex((index) =>
        index == null ? null : (index + 1) % images.length,
      ),
    [images.length],
  );

  useEffect(() => {
    if (activeIndex == null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") previous();
      if (event.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeIndex, next, previous]);

  if (!images.length) return null;
  return (
    <>
      <div
        className={cn(
          "my-16 grid gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3",
          className,
        )}
      >
        {images.map((image, index) => {
          const caption = image.caption?.trim();

          return (
            <figure
              key={`${image.src}-${index}`}
              className="min-w-0 shadow-sm relative rounded-md"
            >
              <button
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Open image ${index + 1} of ${images.length}`}
                className="group relative image-slot block aspect-[8/9] w-full overflow-hidden rounded-lg bg-bg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill
                  sizes="(min-width: 1280px) 620px, (min-width: 640px) 50vw, 100vw"
                  className="object-cover transition-opacity duration-300 group-hover:scale-105"
                />
              </button>

              {caption ? (
                <figcaption className="mt-3 bg-gradient-to-t from-black/50 via-black/30 rounded-b-md to-transparent px-6 pb-6 pt-12 absolute z-10 inset-x-0 bottom-0 flex items-center justify-center gap-3 text-sm leading-relaxed text-muted">
                  <span className="text-pretty drop-shadow-2xl font-display font-bold text-white text-lg">
                    {caption}
                  </span>
                </figcaption>
              ) : null}
            </figure>
          );
        })}
      </div>

      <Dialog.Root
        open={activeIndex != null}
        onOpenChange={(open) => !open && close()}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/95" />
          <Dialog.Content className="fixed inset-0 z-50 flex flex-col p-4 sm:p-6">
            <Dialog.Title className="sr-only">{title}</Dialog.Title>

            <div className="flex items-center justify-between border-b border-white/15 pb-3 text-white">
              <p className="font-mono text-xs tracking-wide text-white/65">
                {activeIndex == null ? 0 : activeIndex + 1} / {images.length}
              </p>
              <Dialog.Close
                aria-label="Close gallery"
                className="grid size-9 place-items-center text-white/75 transition-colors hover:text-white"
              >
                <X size={18} aria-hidden />
              </Dialog.Close>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center py-5 sm:px-12">
              {activeIndex != null ? (
                <figure className="flex h-full w-full max-w-6xl flex-col">
                  <div className="relative min-h-0 flex-1">
                    <Image
                      src={images[activeIndex].src}
                      alt={images[activeIndex].alt}
                      fill
                      sizes="(min-width: 1200px) 1152px, 100vw"
                      className="object-contain"
                    />
                  </div>
                  {images[activeIndex].caption?.trim() ? (
                    <figcaption className="mx-auto mt-4 max-w-3xl border-t border-white/15 pt-3 text-center text-sm leading-relaxed text-white/70">
                      {images[activeIndex].caption?.trim()}
                    </figcaption>
                  ) : null}
                </figure>
              ) : null}

              {images.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={previous}
                    aria-label="Previous image"
                    className="absolute bottom-1 left-0 grid size-10 place-items-center text-white/70 transition-colors hover:text-white sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"
                  >
                    <ArrowLeft size={19} aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next image"
                    className="absolute bottom-1 right-0 grid size-10 place-items-center text-white/70 transition-colors hover:text-white sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"
                  >
                    <ArrowRight size={19} aria-hidden />
                  </button>
                </>
              ) : null}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
